import { useStytchMemberSession } from '@stytch/react/b2b';
import { useCallback, useEffect, useRef } from 'react';
import { isWindowDefined } from 'smoothie';
import { useDebouncedCallback } from 'use-debounce';

import { SESSION_INACTIVITY_THRESHOLD } from '../../auth/authConstants';
import { useLogout } from '../../auth/hooks/useLogout';
import { LogoutType } from '../../auth/types/auth';
import type { ContainerComponent } from '../../common/types/Common';
import { StorageKey } from '../../common/types/Storage';
import { captureMessage } from '../../logger/Logger';

interface TimeoutResetOptions {
    skipRecordingActivity?: boolean;
}

// This component watches for web activity events and will log the user out if it doesn't detect any within the TIMEOUT_MS threshold.
const InactivityDetector: ContainerComponent = ({ children }) => {
    const timeoutRef = useRef<number | NodeJS.Timeout | undefined>(undefined);

    const { logOut } = useLogout();
    const { session } = useStytchMemberSession();

    const TIMEOUT_MS = SESSION_INACTIVITY_THRESHOLD;

    // We need to keep a ref of the session because setTimeout cannot detect state changes.
    const sessionRef = useRef(session);

    // Keep the session ref in sync with state.
    useEffect(() => {
        sessionRef.current = session;
    }, [session]);

    const resetInactivityTimeout = useCallback(
        (options: TimeoutResetOptions = {}) => {
            if (sessionRef.current) {
                // Record activity timestamp to sync across tabs
                if (!options.skipRecordingActivity && isWindowDefined) {
                    localStorage.setItem(
                        StorageKey.ACTIVITY_TIMESTAMP,
                        Date.now().toString(),
                    );
                }

                const newTimer = setTimeout(() => {
                    captureMessage('Attempting logout due to inactivity');
                    void logOut(LogoutType.Inactivity);
                }, TIMEOUT_MS);
                clearTimeout(timeoutRef.current);
                timeoutRef.current = newTimer;
            }
        },
        [logOut, TIMEOUT_MS],
    );

    const debouncedResetInactivityTimeout = useDebouncedCallback(
        () => {
            // wrapping this function to avoid typing conflicts with event handlers
            setTimeout(resetInactivityTimeout, 0);
        },
        1000,
        { leading: true },
    );
    useEffect(() => {
        debouncedResetInactivityTimeout();
        return () => {
            if (timeoutRef.current !== undefined) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [debouncedResetInactivityTimeout]);

    // Detect web activity via window events
    useEffect(() => {
        const webActivityEvents = [
            'load',
            'touchstart',
            'click',
            'mousedown',
            'mouseup',
            'keydown',
            'focus',
            'scroll',
            'resize',
        ];
        if (isWindowDefined) {
            webActivityEvents.forEach((event) => {
                window.addEventListener(
                    event,
                    debouncedResetInactivityTimeout,
                    true,
                );
            });
        }

        return () => {
            if (isWindowDefined) {
                webActivityEvents.forEach((event) => {
                    window.removeEventListener(
                        event,
                        debouncedResetInactivityTimeout,
                        true,
                    );
                });
            }
        };
    }, [debouncedResetInactivityTimeout]);

    // Detect activity in other tabs via localStorage events
    useEffect(() => {
        const handleStorageEvent = (event: StorageEvent) => {
            if (
                event.key === StorageKey.ACTIVITY_TIMESTAMP &&
                event.newValue != null
            ) {
                // Activity detected in another tab, reset timeout without recording
                resetInactivityTimeout({ skipRecordingActivity: true });
            }
        };

        if (isWindowDefined) {
            window.addEventListener('storage', handleStorageEvent, true);
        }

        return () => {
            if (isWindowDefined) {
                window.removeEventListener('storage', handleStorageEvent, true);
            }
        };
    }, [resetInactivityTimeout]);

    return <>{children}</>;
};

export default InactivityDetector;
